import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { ArrowLeft, Save, Plus, Trash2, ClipboardList, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const GRADE_OPTS = ['A', 'A-', 'B+', 'B-', 'C+', 'C'];
const JUZ_OPTS = Array.from({ length: 30 }, (_, i) => i + 1);

// setoran: 0.5, 1, 1.5 ... 20 hal
const SETORAN_OPTS = Array.from({ length: 40 }, (_, i) => {
  const v = (i + 1) * 0.5;
  const whole = Math.floor(v);
  const half = v % 1 !== 0;
  if (half) return whole ? `${whole}½ hal` : '½ hal';
  return `${whole} hal`;
});

function genSemesterOpts(tahunAjaran = '2024/2025') {
  const parts = tahunAjaran.split('/');
  const start = parseInt(parts[0], 10) || 2024;
  const opts = [];
  for (let y = start - 1; y <= start + 1; y++) {
    opts.push(`Ganjil ${y}/${y + 1}`);
    opts.push(`Genap ${y}/${y + 1}`);
  }
  return opts;
}

const EMPTY_MHQ = () => ({ juz: 1, nilai: 'A' });
const EMPTY_MUR = () => ({ juz: 1, setoran: '1 hal', nilai: 'A' });

export default function GuruUjianTahfidz() {
  const navigate = useNavigate();
  const [santriList, setSantriList] = useState([]);
  const [settings, setSettings] = useState({});
  const [activeTab, setActiveTab] = useState('tajwid');
  const [santriId, setSantriId] = useState('');
  const [semester, setSemester] = useState('');
  const [tajwidNilai, setTajwidNilai] = useState('A');
  const [mhqRows, setMhqRows] = useState([EMPTY_MHQ()]);
  const [murRows, setMurRows] = useState([EMPTY_MUR()]);
  const [saving, setSaving] = useState(false);
  const [existingData, setExistingData] = useState(null);
  const [riwayat, setRiwayat] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  const fetchRiwayat = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ujian-tahfidz`, { withCredentials: true });
      setRiwayat(r.data || []);
    } catch (_) {}
  }, []);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/santri`, { withCredentials: true }),
      axios.get(`${API}/settings`),
    ]).then(([s, st]) => {
      setSantriList(s.data);
      const stData = st.data || {};
      setSettings(stData);
      const ta = stData.tahun_ajaran || '2024/2025';
      const sem = stData.semester || 'Ganjil';
      setSemester(`${sem} ${ta}`);
    }).catch(() => {});
    fetchRiwayat();
  }, [fetchRiwayat]);

  const semesterOpts = genSemesterOpts(settings.tahun_ajaran);

  useEffect(() => {
    if (!santriId || !semester) return;
    axios.get(`${API}/ujian-tahfidz?santri_id=${santriId}`, { withCredentials: true })
      .then(r => {
        const found = (r.data || []).find(d => d.semester === semester);
        if (found) {
          setExistingData(found);
          setTajwidNilai(found.tajwid_nilai || 'A');
          setMhqRows(found.mhq?.length ? found.mhq : [EMPTY_MHQ()]);
          setMurRows(found.murojaah?.length ? found.murojaah : [EMPTY_MUR()]);
        } else {
          setExistingData(null);
          setTajwidNilai('A');
          setMhqRows([EMPTY_MHQ()]);
          setMurRows([EMPTY_MUR()]);
        }
      }).catch(() => {});
  }, [santriId, semester]);

  const handleSubmit = async () => {
    if (!santriId) { toast.error('Pilih santri terlebih dahulu'); return; }
    if (!semester) { toast.error('Pilih semester terlebih dahulu'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/ujian-tahfidz`, {
        santri_id: santriId,
        semester,
        tajwid_nilai: tajwidNilai,
        mhq: mhqRows,
        murojaah: murRows,
      }, { withCredentials: true });
      toast.success('Data ujian berhasil disimpan');
      fetchRiwayat();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'tajwid', label: 'Tajwid' },
    { id: 'mhq', label: 'MHQ' },
    { id: 'murojaah', label: 'Murojaah' },
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 pb-28 md:pb-8">
        {/* Header */}
        <div className="bg-gradient-to-br from-emerald-950 to-emerald-800 px-4 pt-12 pb-6">
          <div className="flex items-center gap-3">
            <button data-testid="ujian-back-btn" onClick={() => navigate('/guru')}
              className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors">
              <ArrowLeft size={20} className="text-white" />
            </button>
            <div>
              <h1 className="text-white font-bold text-lg">Ujian Tahfidz</h1>
              <p className="text-emerald-300 text-xs">Input penilaian ujian santri</p>
            </div>
          </div>
        </div>

        <div className="px-4 py-4 space-y-4">
          {/* Santri selector */}
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Santri</label>
            <select data-testid="ujian-santri-select" value={santriId} onChange={e => setSantriId(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white">
              <option value="">-- Pilih Santri --</option>
              {santriList.map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.nis})</option>
              ))}
            </select>
          </div>

          {/* Semester selector */}
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Semester</label>
            <select data-testid="ujian-semester-select" value={semester} onChange={e => setSemester(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white">
              {semesterOpts.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            {existingData && (
              <p data-testid="ujian-existing-warning" className="text-xs text-amber-600 mt-2 font-medium">
                Data semester ini sudah ada — akan digantikan saat disimpan
              </p>
            )}
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="flex border-b border-gray-100">
              {tabs.map(t => (
                <button key={t.id} data-testid={`ujian-tab-${t.id}`}
                  onClick={() => setActiveTab(t.id)}
                  className={`flex-1 py-3 text-sm font-semibold transition-colors ${
                    activeTab === t.id
                      ? 'text-emerald-700 border-b-2 border-emerald-600 bg-emerald-50/50'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>

            <div className="p-4">
              {/* TAJWID */}
              {activeTab === 'tajwid' && (
                <div data-testid="ujian-tajwid-section">
                  <p className="text-xs text-gray-500 mb-4">Penilaian kemampuan tajwid santri</p>
                  <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                    <span className="text-sm text-gray-700 font-medium flex-1">Nilai Tajwid</span>
                    <select data-testid="ujian-tajwid-nilai" value={tajwidNilai}
                      onChange={e => setTajwidNilai(e.target.value)}
                      className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white w-28">
                      {GRADE_OPTS.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {/* MHQ */}
              {activeTab === 'mhq' && (
                <div data-testid="ujian-mhq-section">
                  <p className="text-xs text-gray-500 mb-3">Musabaqoh Hifdzil Quran — setiap juz yang diujikan</p>
                  <div className="space-y-2">
                    {mhqRows.map((row, i) => (
                      <div key={i} data-testid={`ujian-mhq-row-${i}`}
                        className="flex items-end gap-2 bg-gray-50 rounded-xl p-3">
                        <span className="text-xs text-gray-400 w-5 pb-2">{i + 1}</span>
                        <div className="flex-1">
                          <label className="text-[10px] text-gray-400 mb-1 block">Juz</label>
                          <select value={row.juz}
                            onChange={e => { const nr = [...mhqRows]; nr[i] = { ...nr[i], juz: Number(e.target.value) }; setMhqRows(nr); }}
                            className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400 bg-white">
                            {JUZ_OPTS.map(j => <option key={j} value={j}>Juz {j}</option>)}
                          </select>
                        </div>
                        <div className="w-24">
                          <label className="text-[10px] text-gray-400 mb-1 block">Nilai</label>
                          <select value={row.nilai}
                            onChange={e => { const nr = [...mhqRows]; nr[i] = { ...nr[i], nilai: e.target.value }; setMhqRows(nr); }}
                            className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400 bg-white">
                            {GRADE_OPTS.map(g => <option key={g} value={g}>{g}</option>)}
                          </select>
                        </div>
                        {mhqRows.length > 1 && (
                          <button data-testid={`ujian-mhq-del-${i}`}
                            onClick={() => setMhqRows(mhqRows.filter((_, x) => x !== i))}
                            className="pb-1 p-1.5 text-red-400 hover:text-red-600 transition-colors">
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button data-testid="ujian-mhq-add-btn"
                    onClick={() => setMhqRows([...mhqRows, EMPTY_MHQ()])}
                    className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-emerald-300 rounded-xl text-emerald-600 text-sm font-medium hover:bg-emerald-50 transition-colors">
                    <Plus size={16} /> Tambah Baris
                  </button>
                </div>
              )}

              {/* MUROJAAH */}
              {activeTab === 'murojaah' && (
                <div data-testid="ujian-murojaah-section">
                  <p className="text-xs text-gray-500 mb-3">Penilaian murojaah per juz</p>
                  <div className="space-y-2">
                    {murRows.map((row, i) => (
                      <div key={i} data-testid={`ujian-murojaah-row-${i}`}
                        className="bg-gray-50 rounded-xl p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-gray-500">Baris {i + 1}</span>
                          {murRows.length > 1 && (
                            <button data-testid={`ujian-murojaah-del-${i}`}
                              onClick={() => setMurRows(murRows.filter((_, x) => x !== i))}
                              className="p-1 text-red-400 hover:text-red-600 transition-colors">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="text-[10px] text-gray-400 mb-1 block">Juz</label>
                            <select value={row.juz}
                              onChange={e => { const nr = [...murRows]; nr[i] = { ...nr[i], juz: Number(e.target.value) }; setMurRows(nr); }}
                              className="w-full border border-gray-200 rounded-lg px-1.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-400 bg-white">
                              {JUZ_OPTS.map(j => <option key={j} value={j}>Juz {j}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="text-[10px] text-gray-400 mb-1 block">Setoran</label>
                            <select value={row.setoran}
                              onChange={e => { const nr = [...murRows]; nr[i] = { ...nr[i], setoran: e.target.value }; setMurRows(nr); }}
                              className="w-full border border-gray-200 rounded-lg px-1.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-400 bg-white">
                              {SETORAN_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="text-[10px] text-gray-400 mb-1 block">Nilai</label>
                            <select value={row.nilai}
                              onChange={e => { const nr = [...murRows]; nr[i] = { ...nr[i], nilai: e.target.value }; setMurRows(nr); }}
                              className="w-full border border-gray-200 rounded-lg px-1.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-400 bg-white">
                              {GRADE_OPTS.map(g => <option key={g} value={g}>{g}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button data-testid="ujian-murojaah-add-btn"
                    onClick={() => setMurRows([...murRows, EMPTY_MUR()])}
                    className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-emerald-300 rounded-xl text-emerald-600 text-sm font-medium hover:bg-emerald-50 transition-colors">
                    <Plus size={16} /> Tambah Baris
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Submit */}
          <button data-testid="ujian-save-btn" onClick={handleSubmit} disabled={saving}
            className="w-full flex items-center justify-center gap-2 py-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold rounded-2xl transition-colors shadow-sm">
            <Save size={18} />
            {saving ? 'Menyimpan...' : 'Simpan Data Ujian'}
          </button>

          {/* ═══ RIWAYAT DATA UJIAN ═══ */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <ClipboardList size={16} className="text-emerald-600" />
              <h2 className="text-sm font-bold text-gray-700">Riwayat Data Ujian</h2>
              <span className="text-[10px] bg-emerald-100 text-emerald-700 font-bold px-2 py-0.5 rounded-full ml-auto">
                {riwayat.length} data
              </span>
            </div>

            {riwayat.length === 0 ? (
              <div data-testid="ujian-riwayat-empty"
                className="bg-white rounded-2xl p-6 text-center shadow-sm border border-gray-100">
                <ClipboardList size={28} className="text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">Belum ada riwayat ujian</p>
              </div>
            ) : (
              riwayat.map((rec) => {
                const santri = santriList.find(s => s.id === rec.santri_id);
                const isOpen = expandedId === rec.id;
                return (
                  <div key={rec.id} data-testid={`ujian-riwayat-${rec.id}`}
                    className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    {/* Card header — tap to expand */}
                    <button
                      className="w-full flex items-center justify-between px-4 py-3 text-left"
                      onClick={() => setExpandedId(isOpen ? null : rec.id)}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-emerald-700 font-bold text-xs">
                            {santri?.name?.charAt(0) ?? '?'}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">
                            {santri?.name ?? 'Santri tidak ditemukan'}
                          </p>
                          <p className="text-[10px] text-gray-400">{rec.semester}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        {rec.tajwid_nilai && (
                          <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                            T: {rec.tajwid_nilai}
                          </span>
                        )}
                        {isOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                      </div>
                    </button>

                    {/* Expandable detail */}
                    {isOpen && (
                      <div className="border-t border-gray-100 px-4 py-3 space-y-3">

                        {/* Tajwid */}
                        <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Tajwid</p>
                          <span className="inline-block text-sm font-bold text-emerald-700 bg-emerald-50 px-3 py-1 rounded-lg">
                            {rec.tajwid_nilai || '-'}
                          </span>
                        </div>

                        {/* MHQ */}
                        {rec.mhq?.length > 0 && (
                          <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">MHQ</p>
                            <div className="flex flex-wrap gap-1.5">
                              {rec.mhq.map((m, i) => (
                                <span key={i} className="text-xs bg-blue-50 text-blue-700 font-semibold px-2.5 py-1 rounded-lg">
                                  Juz {m.juz} · {m.nilai}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Murojaah */}
                        {rec.murojaah?.length > 0 && (
                          <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Murojaah</p>
                            <div className="space-y-1">
                              {rec.murojaah.map((m, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs">
                                  <span className="text-gray-500 w-14 flex-shrink-0">Juz {m.juz}</span>
                                  <span className="text-gray-400">{m.setoran}</span>
                                  <span className="ml-auto font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-md">{m.nilai}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {rec.catatan && (
                          <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Catatan</p>
                            <p className="text-xs text-gray-600 italic">{rec.catatan}</p>
                          </div>
                        )}

                        <p className="text-[10px] text-gray-300 pt-1">
                          {rec.created_at ? new Date(rec.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
